import { isEmpty, isEqual, startCase } from 'lodash';
import { concat, from, Observable, of, Subject } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { CacheAPI } from '../interfaces/cache-api.interface';
import { CacheChain } from '../interfaces/cache-chain.interface';
import { CoreOptions } from '../interfaces/core-options.interface';
import { Log } from '../interfaces/log.interface';
import { Logger } from '../utils/logger';
import { VERSION } from '../utils/version';
import { Core } from './core';

export class CacheCore<T = any> implements CacheAPI {
    public _memo_cache;
    public _memo_observable;

    public chain: CacheChain = {};
    public options!: CoreOptions;
    public $log: Subject<Log> = new Subject();

    protected logger!: Logger;

    constructor(options: CoreOptions) {
        this.init(options);
    }

    protected log(): Logger {
        return this.logger;
    }

    protected createKey(): string {
        //
        // Defaults for user input key
        if (this.chain.key) return this.chain.key;
        //
        // Use name
        else return this.options.name!;
    }

    protected dispatch(chain: CacheChain, key: string, data: any, observer = { next: (data) => {} }) {
        observer.next(data);

        if (Core.state.enabled && chain.useMemo) {
            const currentState = Core.state.getState(key);

            if (!isEqual(currentState, data)) {
                Core.state.setState(key, data, { save: false });
            }
        }
    }

    protected async setCache(chain: CacheChain, key: string, data: any): Promise<void> {
        //
        // Validate cache is enabled
        if (!chain.useCache || !Core.storage.enabled) return Promise.resolve();

        //
        // - Try to update cache
        // - Catch to log
        try {
            Core.storage.set(key, data);
            this.log().warn()(`${key} cache updated`);
        } catch (err) {
            this.log().danger()(`${key} unable to save cache`);
            if (this.log().enabled()) {
                console.log(err);
            }
        }
    }

    protected getFromStateOrCache<T>(observer, chain, key) {
        const hasState = Core.state.enabled;
        const hasStorage = Core.storage.enabled;

        if (!hasState || !hasStorage) {
            return of();
        }

        return this._getFromStateOrCache(key, chain).pipe(
            take(1),
            tap((payload) => {
                if (payload.data && !this._memo_observable) {
                    this._memo_cache = payload.data;
                    this.dispatch(chain, key, payload.data, observer);
                    this.log().info()(`${key} dispatch from ${payload.from}`);
                    if (this.log().enabled()) {
                        console.table(payload.data);
                    }
                }
            })
        );
    }

    protected getFromObservable<T>(observer, chain: CacheChain, key) {
        if (chain.useObservable) {
            this.log().warn()(`${key} network request start`);
            chain.useObservable.subscribe(
                (response: T) => {
                    if (!isEqual(this._memo_cache, response)) {
                        this.log().info()(`${key} dispatch from observable`);
                        if (this.log().enabled()) {
                            console.table(response);
                        }
                        this._memo_observable = response;
                        this.dispatch(chain, key, response, observer);
                        this.setCache(chain, key, response);
                    }

                    observer.complete();

                    this.log().warn()(`${key} network request end`);
                },
                (err) => {
                    observer.error(err);
                    observer.complete();
                }
            );
        } else {
            this.log().danger()(`${key} observable disabled`);
            if (!chain.useMemo && !chain.useCache) {
                observer.next();
                observer.complete();
            }
        }

        return of();
    }

    private _getFromStateOrCache(key: string, chain): Observable<{ from: 'memo' | 'cache'; data: any }> {
        const hasStore = Core.state.enabled;
        const hasStorage = Core.storage.enabled;

        const useMemo = chain.useMemo;
        const useCache = chain.useCache;

        if (!hasStore && !hasStorage) return of();

        const hasState = hasStore ? Core.state.getState(key) : null;

        return !isEmpty(hasState) && useMemo
            ? of({ from: 'memo', data: hasState })
            : hasStorage && useCache
            ? (from(
                  Core.storage.get(key).then((it) => {
                      return {
                          from: 'cache',
                          data: it,
                      };
                  })
              ) as Observable<any>)
            : of();
    }

    public init(runtime: CoreOptions) {
        //
        // settings which requires runtime evaluation
        const options: CoreOptions = {
            ...Core.options,
            ...runtime,
        };

        //
        // init logger
        this.logger = new Logger({
            subject: this.$log,
            silent: options.silent,
        });

        //
        // log
        const name = options.name;
        this.log().success()(`Stored ${VERSION} Initiated Cache for ${startCase(name)}`);

        //
        // initialize
        this.options = options;
        this.resetChain();
    }

    /**
     * Reset the chaining configuration on the fly
     *
     * @returns {CacheCore}
     * @memberof CacheCore
     */
    public resetChain(): CacheCore {
        this.chain = {
            key: '',
            useCache: this.options.useCache,
            useMemo: this.options.useMemo,
            useObservable: this.options.useObservable,
        };

        return this;
    }

    /**
     * Choose whether to use cached results
     *
     * @param {boolean} active
     * @returns {CacheCore}
     * @memberof CacheCore
     */
    public cache(active: boolean): CacheCore {
        this.chain.useCache = active;
        return this;
    }

    /**
     * Choose whether to use cached results
     *
     * @param {boolean} active
     * @returns {CacheCore}
     * @memberof CacheCore
     */
    public memo(active: boolean): CacheCore {
        this.chain.useMemo = active;
        return this;
    }

    /**
     * Define a custom key to be used as a identifier for the result set
     *
     * @param {string} name
     * @returns {CacheCore}
     * @memberof CacheCore
     */
    public key(name: string): CacheCore {
        this.chain.key = name;
        return this;
    }

    /**
     * Define an observable to get a value after state and cache
     *
     * @param {string} name
     * @returns {CacheCore}
     * @memberof CacheCore
     */
    public observable<T>(_observable: CacheChain['useObservable']): CacheCore {
        this.chain.useObservable = _observable;
        return this;
    }

    public get<T>(): Observable<T> {
        //
        // get attributes
        const key = this.createKey();
        const chain = { ...this.chain };

        // reset to not mess the chain of subsequent calls
        this.resetChain();

        /**
         * order for response without repetition
         * 1 - state
         * 2 - cache
         * 3 - observable
         */
        return new Observable((observer) => {
            concat(this.getFromStateOrCache(observer, chain, key), this.getFromObservable(observer, chain, key))
                .pipe(
                    //
                    // ensure that we'll have at most two responses
                    take(2)
                )
                .subscribe();
        });
    }
}

export class Cache extends CacheCore {}
